import React from "react";
import useReducedMotion from "./useReducedMotion";

// Returns a `hidden` boolean that flips to `true` when the user scrolls *down*
// past `topThreshold`, and back to `false` as soon as they scroll *up* past
// `deltaThreshold`. Shared by the site header on the rebranded pages (Medium /
// YouTube / Twitter-style auto-hide chrome) and cheap enough to embed anywhere
// else that wants the same behavior.
//
// Options:
//   - topThreshold (default 80px) — when the page is near the top, the header
//     is always shown. This keeps the first viewport feeling "full" and avoids
//     an abrupt hide triggered by the first small scroll.
//   - deltaThreshold (default 8px) — minimum frame-to-frame movement needed
//     to flip state. Prevents flicker from subpixel scroll events and
//     trackpad inertia micro-movements.
//   - disabled (default false) — force the hook to keep `hidden = false`.
//     Wire to states like "mobile sidebar open" or "modal open" so the header
//     (which contains the close button) never disappears while it's needed.
//
// Notes:
//   - SSR-safe: returns `false` before any effect runs (no `window` access).
//   - Respects `prefers-reduced-motion`: if the user prefers reduced motion
//     we never return `true`, so the header stays put.
//   - One passive `scroll` listener per consumer, coalesced to one rAF tick
//     per frame. State only flips on actual direction change, so React
//     re-renders are minimal.
export default function useHideOnScrollDown({
  topThreshold = 80,
  deltaThreshold = 8,
  disabled = false,
} = {}) {
  const reducedMotion = useReducedMotion();
  const [hidden, setHidden] = React.useState(false);
  const lastYRef = React.useRef(0);
  const tickingRef = React.useRef(false);

  // When the hook is disabled (e.g. mobile sidebar open) or reduced-motion is
  // on, snap back to visible and skip installing the scroll listener.
  React.useEffect(() => {
    if (disabled || reducedMotion) {
      setHidden(false);
    }
  }, [disabled, reducedMotion]);

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (disabled || reducedMotion) return undefined;

    // Prime lastY at the topThreshold boundary so the *first* hide needs a
    // full `deltaThreshold` of downward movement past the boundary — avoids
    // an abrupt hide the instant the user scrolls 80 + epsilon pixels from
    // the top of the page.
    lastYRef.current = Math.max(topThreshold, window.scrollY);

    const update = () => {
      tickingRef.current = false;
      const y = window.scrollY;

      if (y < topThreshold) {
        setHidden(false);
        lastYRef.current = topThreshold;
        return;
      }

      const delta = y - lastYRef.current;
      if (delta > deltaThreshold) {
        setHidden(true);
        lastYRef.current = y;
      } else if (delta < -deltaThreshold) {
        setHidden(false);
        lastYRef.current = y;
      }
      // Within the deadband: let lastY stand so slow drift accumulates into
      // a meaningful delta over a few frames instead of getting swallowed.
    };

    const onScroll = () => {
      if (tickingRef.current) return;
      tickingRef.current = true;
      window.requestAnimationFrame(update);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, [disabled, reducedMotion, topThreshold, deltaThreshold]);

  return hidden;
}
